import { useState } from "react";
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useApiClient } from "@/lib/auth";
import { router } from "@/router";
import { useAppStore } from "@/store";
import { useToast } from "@/hooks/use-toast";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Route = createFileRoute("/signup")({
  component: SignupPage,
});

function SignupPage() {
  const { t } = useTranslation(["auth", "common"]);
  const navigate = useNavigate();
  const api = useApiClient();
  const setAuth = useAppStore((s) => s.setAuth);
  const { apiError: showApiError } = useToast();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const signupMutation = useMutation({
    mutationFn: async (creds: { full_name: string; email: string; password: string }) => {
      const { data, error } = await api.POST("/auth/signup", {
        body: creds,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: async (data) => {
      if (data) {
        setAuth(
          data.access_token,
          {
            id: data.user.id,
            email: data.user.email,
            role: data.user.role,
            emailVerified: data.user.email_verified,
          },
          data.refresh_token,
        );
        // The new account's token names no org; an org an earlier session left
        // in the persisted store must not ride along into it.
        useAppStore.getState().clearActiveOrg();
        useAppStore.getState().setExpiresAt(new Date(data.expires_at).getTime());
        // Sync theme preference from server (parity with login.tsx)
        const theme = data.user.preferences?.theme;
        if (theme === "system" || theme === "light" || theme === "dark") {
          useAppStore.getState().setThemePreference(theme);
        }
        // Sync locale preference from server
        const locale = data.user.preferences?.locale;
        if (locale === "system" || locale === "en" || locale === "es") {
          useAppStore.getState().setLocalePreference(locale);
        }
        // Sync accent color from server
        const accentColor = data.user.preferences?.accent_color;
        if (accentColor) {
          useAppStore.getState().setAccentColor(accentColor);
        }
        await router.invalidate();
        void navigate({ to: "/mfa-prompt" });
      }
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      showApiError(code);
    },
  });

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const full_name = (formData.get("full_name") as string).trim();
    const email = (formData.get("email") as string).trim();
    const password = formData.get("password") as string;
    const confirmPassword = formData.get("confirm_password") as string;

    const errors: Record<string, string> = {};
    if (!full_name) {
      errors.full_name = t("validation.required");
    }
    if (!email) {
      errors.email = t("validation.required");
    } else if (!EMAIL_RE.test(email)) {
      errors.email = t("validation.invalidEmail");
    }
    if (!password) {
      errors.password = t("validation.required");
    } else if (password.length < 8) {
      errors.password = t("validation.minLength", { min: 8 });
    }
    if (!confirmPassword) {
      errors.confirm_password = t("validation.required");
    } else if (password && confirmPassword !== password) {
      errors.confirm_password = t("passwordMismatch");
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    signupMutation.mutate({ full_name, email, password });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-surface">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-lg dark:bg-gray-800">
        <div>
          <h2 className="text-center text-3xl font-bold text-on-surface">
            {t("createAccountTitle")}
          </h2>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label htmlFor="full_name" className="block text-sm font-medium text-muted">
                {t("fullNameLabel")}
              </label>
              <input
                id="full_name"
                name="full_name"
                type="text"
                autoComplete="name"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.full_name && (
                <p className="mt-1 text-xs text-danger">{fieldErrors.full_name}</p>
              )}
            </div>
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-muted">
                {t("emailLabel")}
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.email && <p className="mt-1 text-xs text-danger">{fieldErrors.email}</p>}
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-muted">
                {t("passwordLabel")}
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              <p className="mt-1 text-xs text-muted">{t("passwordMinLength")}</p>
              {fieldErrors.password && (
                <p className="mt-1 text-xs text-danger">{fieldErrors.password}</p>
              )}
            </div>
            <div>
              <label htmlFor="confirm_password" className="block text-sm font-medium text-muted">
                {t("confirmPasswordLabel")}
              </label>
              <input
                id="confirm_password"
                name="confirm_password"
                type="password"
                autoComplete="new-password"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-600 dark:bg-gray-700 dark:text-on-surface"
              />
              {fieldErrors.confirm_password && (
                <p className="mt-1 text-xs text-danger">{fieldErrors.confirm_password}</p>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={signupMutation.isPending}
            className="w-full rounded-md bg-primary px-4 py-2 text-on-primary hover:bg-primary-hover focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-50"
          >
            {signupMutation.isPending ? t("creatingAccount") : t("createAccount")}
          </button>
        </form>

        <p className="text-center text-sm text-muted">
          {t("hasAccount")}{" "}
          <Link to="/login" className="font-medium text-primary hover:text-primary-hover">
            {t("signInLink")}
          </Link>
        </p>
      </div>
    </div>
  );
}
